import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import lscache from "lscache";
import React, { ChangeEvent, SyntheticEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import IJob from "../../types/job-type";
import AddressFields from "../Forms/Address";
import CalendarSelect from "../Forms/CalendarSelect";
import TitleDescription from "../Forms/TitleDescription";
import GroupedCountries, {
  GroupedCurrencies,
} from "../Handlers/CountryHandler";

interface JobFormProps {
  editing: boolean;
  formData: IJob;
  handleSubmit: (e: React.FormEvent) => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const JobForm: React.FC<JobFormProps> = ({
  editing,
  formData,
  setFormData,
  handleSubmit,
}) => {
  const intl = useIntl();
  const jobTypes = ["full-time", "part-time", "contract", "freelance"] as const;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("job-" + name, value, 30);
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("job-" + name, value, 30);
    console.log(formData)
  };

  const handleCountryChange = (
    event: SyntheticEvent,
    newValue: string | null,
    newCurrency: string | null
  ) => {
    setFormData((prevState: string[]) => ({
      ...prevState,
      country: newValue || "",
      currency: newCurrency || "",
    }));
    lscache.set("job-country", newValue, 30);
    lscache.set("job-currency", newCurrency, 30);
  };

  const handleCurrencyChange = (
    event: SyntheticEvent,
    newCurrency: string | null
  ) => {
    setFormData({ ...formData, currency: newCurrency || "" });
    lscache.set("job-currency", newCurrency, 30);
  };

  const currencySymbol = Intl.NumberFormat("en-US", {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency: formData.currency || "EUR",
  })
    .formatToParts(0)
    .filter((part) => part.type === "currency")
    .map((part) => part.value)
    .join("");

  const handleDateChange = (date: dayjs.Dayjs, name: string) => {
    if (date) {
      setFormData({ ...formData, [name]: date });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleDescription
            title={formData.role}
            description={formData.description}
            handleChange={handleChange}
            fieldName="role"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="company"
            type="text"
            value={formData.company}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "company",
              defaultMessage: "Company",
            })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="salary"
            type="number"
            value={formData.salary}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "salary",
              defaultMessage: "Salary",
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <FormattedMessage id="per-month" />
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <GroupedCurrencies
            currency={formData.currency!}
            handleCurrencyChange={handleCurrencyChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl component="fieldset" style={{ display: "flex" }}>
            <FormLabel
              component="legend"
              style={{ height: "0px", fontSize: 10 }}
            >
              <FormattedMessage id="job-types" />
            </FormLabel>

            <RadioGroup
              name="jobType"
              row
              value={formData.jobType || "full-time"}
              onChange={handleTypeChange}
              style={{ display: "flex" }}
            >
              {jobTypes.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Radio
                      checked={formData.jobType === type}
                      value={type}
                      name="jobType"
                    />
                  }
                  label={intl.formatMessage({
                    id: type,
                    defaultMessage: type,
                  })}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={9}>
          <AddressFields
            address={formData.address}
            city={formData.city}
            handleChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <GroupedCountries
            country={formData.country!}
            handleCountryChange={handleCountryChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CalendarSelect
            start={dayjs(formData.startingDate)}
            handleDateChange={(date) => handleDateChange(date, "startingDate")}
            name="startingDate"
            id="starting-date"
            message="StartingDate"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4} mb={2}>
          <Button variant="contained" type="submit" fullWidth>
            {editing ? (
              <FormattedMessage id="update-job" />
            ) : (
              <FormattedMessage id="submit-job" />
            )}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default JobForm;
